/* eslint-disable @typescript-eslint/no-explicit-any */
import axiosInstance from "@api/axiosInstance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";


interface IGETAPI {
  key: string
  url: string,
  pathVariables?: any,  // Handle dynamic paths
  params?: any,           // Handle query parameters
  enabled?: boolean,
  queryOptions?: any,     // Additional react-query options
}
export const useGetApi: any = ({
  key,
  url,
  pathVariables = {},  // Handle dynamic paths
  params = {},           // Handle query parameters
  enabled = true,
  queryOptions = {},     // Additional react-query options
}: IGETAPI) => {
  // Replace URL path variables
  const formattedUrl = Object.keys(pathVariables).reduce(
    (acc, key) => acc.replace(`:${key}`, pathVariables[key]),
    url
  );

  return useQuery({
    queryKey: [key, formattedUrl, params],  // Cache based on URL & params
    queryFn: async () => {
      const response = await axiosInstance.get(formattedUrl, { params });
      return response.data;
    },
    enabled,
    retry: 1,
    ...queryOptions,  // Allow additional options
  });
};


// ✅ Generic POST, PUT, DELETE Hook (Mutation) with Optimistic Updates + Params
export const useMutationApi = (
  method: "post" | "put" | "delete",
  url: string,
  key?: string,
  params?: Record<string, any>,
  meta?: any
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      // ✅ Handle methods differently because "delete" often uses { data, params } shape
      const response =
        method === "delete"
          ? await axiosInstance.delete(url, { data, params })
          : await axiosInstance[method](url, data, { params });

      return response.data;
    },
    meta: meta,
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: [key] });

      const previousData = queryClient.getQueryData([key]);

      queryClient.setQueryData([key], (old: any) => [...(old || []), newData]);

      return { previousData };
    },

    onError: (_error, _newData, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([key], context.previousData);
      }
    },

    onSuccess: () => {
      if (key) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  return mutation;
};


//for file uploading
export const useFileMutation = (baseUrl: string, key?: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ method, data, fileId }: { method: "post" | "delete"; data: FormData | null; fileId?: number }) => {
      const url = method === "delete" && fileId ? `${baseUrl}/${fileId}` : baseUrl;
      const config = {
        method,
        url,
        data: method === "post" ? data : undefined,
        headers: method === "post" ? { "Content-Type": "multipart/form-data" } : undefined,
      };
      const response = await axiosInstance(config);
      return response.data;
    },
    onMutate: async () => {
      if (key) {
        await queryClient.cancelQueries({ queryKey: [key] });
      }
    },
    onError: (_error, _newData, context: any) => {
      if (context?.previousData && key) {
        queryClient.setQueryData([key], context.previousData);
      }
    },
    onSuccess: () => {
      if (key) {
        queryClient.invalidateQueries({ queryKey: [key] }); // Refetch related queries
      }
    },
  });

  return mutation; // Return the mutation object
};


interface IPaginatedAPI {
  key: string;
  url: string;
  page: number;
  limit: number;
  pathVariables?: Record<string, string | number>; // for dynamic :id etc.
  params?: Record<string, any>;                    // extra query params
  enabled?: boolean;
  queryOptions?: Record<string, any>;               // additional react-query options
}

export const usePaginatedApi = ({
  key,
  url,
  page,
  limit,
  pathVariables = {},
  params = {},
  enabled = true,
  queryOptions = {},
}: IPaginatedAPI) => {
  // Replace dynamic URL variables
  const formattedUrl = Object.keys(pathVariables).reduce(
    (acc, k) => acc.replace(`:${k}`, String(pathVariables[k])),
    url
  );

  return useQuery({
    queryKey: [key, formattedUrl, page, limit, params], // cache per page & params
    queryFn: async () => {
      const response = await axiosInstance.get(formattedUrl, {
        params: { page, limit, ...params },
      });
      return response.data;
    },
    enabled,
    staleTime: 5000,
    retry: 1,
    ...queryOptions,
  });
};


export const usePrefetchData = (key: string, url: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.prefetchQuery({ queryKey: [key], queryFn: () => axiosInstance.get(url).then((res) => res.data) });
};

export const useManualApi = (key: string, url: string) => {
  return useQuery({
    queryKey: [key],
    queryFn: async () => {
      const response = await axiosInstance.get(url);
      return response.data;
    },
    enabled: false,
  });
};


